import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import type { ReactNode, RefObject } from 'react';

export const enum HelpPosition {
  Top,
  Right,
  Bottom,
  Left
}

export interface HelpScenario {
  id: string;
  mask?: boolean;
  header: string;
  content: string;
  position?: HelpPosition[];
}

interface HelpActions {
  trigger: (id: string) => void;
  onTrigger: (cb: (id: string) => void) => () => void;
  getAvailableElement: (id: string) => Element | null;
  mountElement: (el: Element, helpId: string) => void;
  unmountElement: (el: Element, helpId: string) => void;
}

const HelpContext = createContext<HelpActions | null>(null);
HelpContext.displayName = 'Help';

export function HelpProvider({ children }: { children: ReactNode }) {
  const [mountedMap, setMountedMap] = useState(
    () => new Map<string, Element[]>()
  );
  const triggerCbs = useRef(new Set<(id: string) => void>());
  const mountedMapRef = useRef(mountedMap);
  mountedMapRef.current = mountedMap;

  const trigger = useCallback((id: string) => {
    triggerCbs.current.forEach((cb) => cb(id));
  }, []);

  const onTrigger = useCallback((cb: (id: string) => void) => {
    // Note: this is intended to be called inside useEffect by consumers
    triggerCbs.current.add(cb);
    return () => triggerCbs.current.delete(cb);
  }, []);

  const getAvailableElement = useCallback((id: string): Element | null => {
    return mountedMapRef.current.get(id)?.[0] ?? null;
  }, []);

  const mountElement = useCallback((el: Element, helpId: string) => {
    setMountedMap((prev) => {
      const next = new Map(prev);
      const mounted = next.get(helpId);
      if (mounted) {
        mounted.push(el);
      } else {
        next.set(helpId, [el]);
      }
      return next;
    });
  }, []);

  const unmountElement = useCallback((el: Element, helpId: string) => {
    setMountedMap((prev) => {
      const next = new Map(prev);
      const mounted = next.get(helpId);
      if (!mounted) {
        return prev;
      }
      const idx = mounted.indexOf(el);
      if (idx < 0) {
        return prev;
      }
      const newMounted = [...mounted];
      newMounted.splice(idx, 1);
      if (newMounted.length === 0) {
        next.delete(helpId);
      } else {
        next.set(helpId, newMounted);
      }
      return next;
    });
  }, []);

  const actions = useMemo(
    () => ({
      trigger,
      onTrigger,
      getAvailableElement,
      mountElement,
      unmountElement
    }),
    [getAvailableElement, mountElement, onTrigger, trigger, unmountElement]
  );

  return (
    <HelpContext.Provider value={actions}>{children}</HelpContext.Provider>
  );
}

export function useHelp(): HelpActions {
  const ctx = useContext(HelpContext);
  if (!ctx) {
    throw new Error('Help not provided');
  }
  return ctx;
}

/**
 * Hook that registers a DOM element with the help system for a given helpId.
 * Returns a ref to attach to the target DOM element.
 */
export function useHelpRef<T extends Element = HTMLElement>(
  helpId: string
): RefObject<T | null> {
  const help = useHelp();
  const ref = useRef<T>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) {
      return;
    }
    help.mountElement(el, helpId);
    return () => help.unmountElement(el, helpId);
  }, [help, helpId]);

  return ref;
}

/**
 * Hook that subscribes to help triggers while the component is mounted.
 */
export function useHelpTrigger(cb: (id: string) => void) {
  const help = useHelp();
  const cbRef = useRef(cb);
  cbRef.current = cb;

  useEffect(() => {
    const fn = (id: string) => cbRef.current(id);
    const unsubscribe = help.onTrigger(fn);
    return unsubscribe;
  }, [help]);
}

/**
 * Hook that auto-focuses an element when shouldFocus is true.
 * Replaces the Vue v-focus directive.
 */
export function useFocusOnMount(
  shouldFocus: boolean
): RefObject<HTMLElement | null> {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (shouldFocus && ref.current) {
      ref.current.focus();
    }
  }, [shouldFocus]);

  return ref;
}
